import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import { apiFetch } from '../../lib/api';
import BACKEND_URL from '../../config.js';
import { Posizione, Candidato, StatoCandidatura, StatoPosizione } from '../../types';
import '../../styles/ats.css';

const COLONNE: { stato: StatoCandidatura; label: string }[] = [
  { stato: 'RICEVUTO',    label: 'Ricevuto' },
  { stato: 'SCREENING',   label: 'Screening' },
  { stato: 'COLLOQUIO_1', label: 'Colloquio 1' },
  { stato: 'COLLOQUIO_2', label: 'Colloquio 2' },
  { stato: 'OFFERTA',     label: 'Offerta' },
  { stato: 'ASSUNTO',     label: 'Assunto' },
  { stato: 'RIFIUTATO',   label: 'Rifiutato' },
];

const STATO_POSIZIONE_LABEL: Record<StatoPosizione, string> = {
  APERTA:  'Aperta',
  CHIUSA:  'Chiusa',
  SOSPESA: 'Sospesa',
};

export default function DettaglioPosizione() {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();

  const [posizione,   setPosizione]   = useState<Posizione | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  /* Drag & Drop */
  const draggingId = useRef<number | null>(null);

  /* Modale aggiungi candidato */
  const [showModal,  setShowModal]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [modalError, setModalError] = useState('');
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', telefono: '', note: '' });
  const [cvFile, setCvFile] = useState<File | null>(null);

  /* Chiudi posizione */
  const [showChiudi,  setShowChiudi]  = useState(false);
  const [nuovoStato,  setNuovoStato]  = useState<StatoPosizione>('CHIUSA');
  const [savingStato, setSavingStato] = useState(false);

  const load = async () => {
    const data = await apiFetch<{ posizione: Posizione }>(`/api/ats/posizioni/${id}`);
    setPosizione(data.posizione);
  };

  useEffect(() => {
    setLoading(true);
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  /* ── Drag & Drop ─────────────────────────────────────────────────────── */
  const handleDragStart = (candidatoId: number) => {
    draggingId.current = candidatoId;
  };

  const handleDrop = async (nuovoStato: StatoCandidatura) => {
    const cid = draggingId.current;
    draggingId.current = null;
    if (!cid || !posizione) return;

    const candidato = posizione.candidati?.find((c) => c.id === cid);
    if (!candidato || candidato.statoCandidatura === nuovoStato) return;

    // Aggiorna UI ottimisticamente
    setPosizione((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        candidati: prev.candidati?.map((c) =>
          c.id === cid ? { ...c, statoCandidatura: nuovoStato } : c
        ),
      };
    });

    try {
      const res = await apiFetch<{ candidato: Candidato; assunto?: boolean; prefillDipendente?: any }>(
        `/api/ats/candidati/${cid}/stato`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stato: nuovoStato }),
        }
      );
      if (res.assunto && res.prefillDipendente) {
        const ok = window.confirm(
          `${candidato.nome} ${candidato.cognome} è stato marcato come Assunto.\n\nVuoi creare subito la scheda dipendente?`
        );
        if (ok) {
          navigate('/dipendenti/nuovo', { state: { prefill: res.prefillDipendente } });
        }
      }
    } catch {
      // Rollback
      setPosizione((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          candidati: prev.candidati?.map((c) =>
            c.id === cid ? { ...c, statoCandidatura: candidato.statoCandidatura } : c
          ),
        };
      });
    }
  };

  /* ── Aggiungi candidato ──────────────────────────────────────────────── */
  const openModal = () => {
    setForm({ nome: '', cognome: '', email: '', telefono: '', note: '' });
    setCvFile(null);
    setModalError('');
    setShowModal(true);
  };

  const handleAddCandidato = async () => {
    if (!form.nome.trim() || !form.cognome.trim() || !form.email.trim()) {
      setModalError('Nome, cognome ed email sono obbligatori.');
      return;
    }
    setSaving(true);
    setModalError('');
    try {
      const fd = new FormData();
      fd.append('nome',      form.nome.trim());
      fd.append('cognome',   form.cognome.trim());
      fd.append('email',     form.email.trim());
      fd.append('telefono',  form.telefono.trim());
      fd.append('note',      form.note.trim());
      if (cvFile) fd.append('cv', cvFile);

      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/ats/posizioni/${id}/candidati`,
        { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd }
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Errore ${res.status}`);
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      setModalError(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* ── Cambia stato posizione ──────────────────────────────────────────── */
  const handleCambiaStato = async () => {
    setSavingStato(true);
    try {
      await apiFetch(`/api/ats/posizioni/${id}/stato`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stato: nuovoStato }),
      });
      setShowChiudi(false);
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSavingStato(false);
    }
  };

  if (loading) return <Layout title="Posizione"><div className="inline-loading">Caricamento…</div></Layout>;
  if (error || !posizione) return <Layout title="Posizione"><div className="inline-error">{error || 'Non trovata'}</div></Layout>;

  const candidatiPerStato: Record<StatoCandidatura, Candidato[]> = {} as any;
  COLONNE.forEach((c) => { candidatiPerStato[c.stato] = []; });
  (posizione.candidati ?? []).forEach((c) => {
    if (candidatiPerStato[c.statoCandidatura]) {
      candidatiPerStato[c.statoCandidatura].push(c);
    }
  });

  return (
    <Layout title={posizione.titolo}>
      {/* Header */}
      <BopBackLink to="/ats/posizioni" label="Torna alle posizioni" />
      <div className="ats-dettaglio-header">
        <div className="ats-header-spacer" />
        {posizione.societaNome && (
          <span className="ats-dettaglio-societa">{posizione.societaNome}</span>
        )}
        <span className={`badge ${posizione.stato === 'APERTA' ? 'badge-green' : posizione.stato === 'SOSPESA' ? 'badge-yellow' : 'badge-slate'}`}>
          {STATO_POSIZIONE_LABEL[posizione.stato]}
        </span>
        <button className="btn btn-ghost" onClick={() => { setNuovoStato('CHIUSA'); setShowChiudi(true); }}>
          Cambia stato
        </button>
        {posizione.stato === 'APERTA' && (
          <button className="btn btn-primary" onClick={openModal}>+ Aggiungi Candidato</button>
        )}
      </div>

      {/* Kanban board */}
      <div className="ats-kanban">
        {COLONNE.map(({ stato, label }) => (
          <div
            key={stato}
            className="ats-kanban-column"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(stato)}
          >
            {/* Header colonna */}
            <div className={`ats-kanban-header ats-kanban-header--${stato.toLowerCase()}`}>
              <span>{label}</span>
              <span className="ats-kanban-count">
                {candidatiPerStato[stato].length}
              </span>
            </div>

            {/* Cards candidati */}
            <div className="ats-kanban-body">
              {candidatiPerStato[stato].map((c) => (
                <div
                  key={c.id}
                  draggable
                  onDragStart={() => handleDragStart(c.id)}
                  onClick={() => navigate(`/ats/candidati/${c.id}`)}
                  className="ats-candidato-card"
                >
                  <div className="ats-candidato-nome">{c.cognome} {c.nome}</div>
                  <div className="ats-candidato-email">{c.email}</div>
                  {c.responsabileNome && (
                    <div className="ats-candidato-responsabile">👤 {c.responsabileNome}</div>
                  )}
                </div>
              ))}
              {candidatiPerStato[stato].length === 0 && (
                <div className="ats-kanban-empty">
                  Trascina qui
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modale aggiungi candidato */}
      {showModal && (
        <div className="modal-backdrop" onClick={() => !saving && setShowModal(false)}>
          <div className="modal ats-modal-width-500" onClick={(e) => e.stopPropagation()}>
            <h3 className="ats-modal-title">Aggiungi Candidato</h3>
            {modalError && <div className="inline-error ats-modal-error">{modalError}</div>}

            <div className="ats-modal-form-grid">
              <div>
                <label className="form-label">Nome *</label>
                <input className="bop-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Cognome *</label>
                <input className="bop-input" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
              </div>
            </div>

            <div className="ats-modal-field">
              <label className="form-label">Email *</label>
              <input className="bop-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>

            <div className="ats-modal-field">
              <label className="form-label">Telefono</label>
              <input className="bop-input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            </div>

            <div className="ats-modal-field">
              <label className="form-label">CV (PDF, DOC, max 10 MB)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                className="bop-input"
                onChange={(e) => setCvFile(e.target.files?.[0] ?? null)}
              />
            </div>

            <div className="ats-modal-note-field">
              <label className="form-label">Note</label>
              <textarea className="bop-input" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>

            <div className="ats-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)} disabled={saving}>Annulla</button>
              <button className="btn btn-primary" onClick={handleAddCandidato} disabled={saving}>
                {saving ? 'Salvataggio…' : 'Aggiungi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale cambia stato posizione */}
      {showChiudi && (
        <div className="modal-backdrop" onClick={() => !savingStato && setShowChiudi(false)}>
          <div className="modal ats-modal-width-360" onClick={(e) => e.stopPropagation()}>
            <h3 className="ats-modal-title">Cambia stato posizione</h3>
            <div className="ats-modal-note-field">
              <label className="form-label">Nuovo stato</label>
              <select className="bop-select" value={nuovoStato} onChange={(e) => setNuovoStato(e.target.value as StatoPosizione)}>
                <option value="APERTA">Aperta</option>
                <option value="SOSPESA">Sospesa</option>
                <option value="CHIUSA">Chiusa</option>
              </select>
            </div>
            <div className="ats-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowChiudi(false)} disabled={savingStato}>Annulla</button>
              <button className="btn btn-primary" onClick={handleCambiaStato} disabled={savingStato}>
                {savingStato ? 'Salvataggio…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
